import React from "react";
import { alpha, Chip, Container, Skeleton, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import styles from "./assignedLotsTableStyles.js";
import { RecordCircle } from "iconsax-react";
import { blue } from "@mui/material/colors";
import LotsTableAssignedActions from "../LotsTableAssignedActions/index.js";

const AssignedLotsTable = ({ lots = [], isLoading, fetchLots, search, setHasItemsSelected, setSelectedLots, selectedLots = [] }) => {

  function StatusIcon({ status }) {
    if (status === "Asignado a Repartidor") {
      return <Chip
        sx={{
          color: '#53CA98',
          backgroundColor: alpha(
            '#53CA98',
            0.1
          ),
        }}
        icon={
          <RecordCircle size="12" variant="Bold"/>
        }
        label="Asignado"
        size="small"
      />
    } else {
      return <Chip
        sx={{
          color: blue[500],
          backgroundColor: alpha(
            blue[500],
            0.1
          ),
        }}
        icon={
          <RecordCircle size="12" variant="Bold"/>
        }
        label="Recolectado"
        size="small"
      />
    }
  }

  const columns = [
    {
      field: "id",
      headerName: "NO. LOTE",
      width: 90,
      resizable: false,
      sortable: false,
      renderHeader: (params) => <strong>NO. LOTE</strong>,
    },
    {
      field: "actual_owner_name",
      headerName: "NOMBRE",
      minWidth: 200,
      flex: 1,
      resizable: false,
      sortable: false,
      renderHeader: (params) => <strong>NOMBRE</strong>,
    },
    {
      field: "actual_status",
      headerName: "ESTATUS",
      type: "string",
      width: 130,
      resizable: false,
      sortable: false,
      headerAlign: "left",
      align: "left",
      renderHeader: (params) => <strong>ESTATUS</strong>,
      renderCell: (params) => <StatusIcon status={params.value} />,
    },
    {
      field: "area",
      headerName: "AREA",
      type: "string",
      width: 90,
      resizable: false,
      sortable: false,
      headerAlign: "left",
      align: "left",
      renderHeader: (params) => <strong>AREA</strong>,
    },
    {
      field: "zone",
      headerName: "ZONA",
      type: "string",
      width: 90,
      resizable: false,
      sortable: false,
      headerAlign: "left",
      align: "left",
      renderHeader: (params) => <strong>ZONA</strong>,
    },
    {
      field: "sector",
      headerName: "SECTOR",
      type: "string",
      width: 90,
      resizable: false,
      sortable: false,
      headerAlign: "left",
      align: "left",
      renderHeader: (params) => <strong>SECTOR</strong>,
    },
    {
      field: "subsector",
      headerName: "SUB",
      type: "string",
      width: 70,
      resizable: false,
      sortable: false,
      headerAlign: "left",
      align: "left",
      renderHeader: (params) => <strong>SUB</strong>,
    },
    {
      field: "total_guides",
      headerName: "GUIAS",
      type: "string",
      width: 70,
      resizable: false,
      sortable: false,
      headerAlign: "left",
      align: "left",
      renderHeader: (params) => <strong>GUIAS</strong>,
    },
    {
      field: "total_weight",
      headerName: "PESO",
      type: "string",
      width: 60,
      resizable: false,
      sortable: false,
      headerAlign: "left",
      align: "left",
      renderHeader: (params) => <strong>PESO</strong>,
    },
    {
      field: "actions",
      headerName: "ACCIONES",
      type: "string",
      width: 100,
      resizable: false,
      sortable: false,
      headerAlign: "left",
      align: "left",
      renderHeader: (params) => <strong>ACCIONES</strong>,
      renderCell: (params) => <LotsTableAssignedActions fetchLots={fetchLots} lot={params.row} />,
    },
  ];

  const filteredRows = lots.filter((guide) => {
    return Object.values(guide).some((value) =>
      value.toString().toLowerCase().includes(search.toLowerCase())
    );
  });

  const rows = filteredRows?.map((lot, index) => ({
    id: lot.id_batch,
    actual_owner_name: lot.actual_owner_name,
    zone: lot.zone ?? 'Sin zona',
    area: lot.area ?? 'Sin area',
    sector: lot.sector ?? 'Sin sector',
    subsector: lot.subsector ?? 'Sin subsector',
    postal_code: lot.postal_code,
    total_guides: lot.count_guides,
    total_weight: lot.count_weight,
    actual_status: lot.actual_status,
    s3_qrcode: lot.s3_qrcode,
  }));

  const handleSelectionChange = (newSelection) => {
    // Combinar las nuevas selecciones con las selecciones previas que no están visibles
    const currentVisibleIds = rows.map(row => row.id);
    const previousNonVisibleSelections = selectedLots.filter(id => 
      !currentVisibleIds.includes(id)
    );
    
    const combinedSelections = [...new Set([...previousNonVisibleSelections, ...newSelection])];
    
    setSelectedLots(combinedSelections);
    setHasItemsSelected(combinedSelections.length > 0);
  };

  // Solo las selecciones que son visibles en las filas actuales
  const visibleSelections = selectedLots.filter(id => 
    rows.some(row => row.id === id)
  );

  return (
    <Container sx={styles.mainContainer} style={styles.principalContainer}>
      {isLoading ? (
        <Container sx={styles.skeletonMainContainer}>
          <Container sx={styles.skeletonSecondaryContainer}>
            <Skeleton variant="rounded" width={'5%'} height={40} />
            <Skeleton variant="rounded" width={'5%'} height={40} />
            <Skeleton variant="rounded" width={'20%'} height={40} />
            <Skeleton variant="rounded" width={'10%'} height={40} />
            <Skeleton variant="rounded" width={'10%'} height={40} />
            <Skeleton variant="rounded" width={'10%'} height={40} />
            <Skeleton variant="rounded" width={'10%'} height={40} />
            <Skeleton variant="rounded" width={'10%'} height={40} />
            <Skeleton variant="rounded" width={'10%'} height={40} />
            <Skeleton variant="rounded" width={'15%'} height={40} />
          </Container>
          <Container sx={styles.skeletonSecondaryContainer}>
            <Skeleton variant="rounded" width={'5%'} height={300} />
            <Skeleton variant="rounded" width={'5%'} height={300} />
            <Skeleton variant="rounded" width={'20%'} height={300} />
            <Skeleton variant="rounded" width={'10%'} height={300} />
            <Skeleton variant="rounded" width={'10%'} height={300} />
            <Skeleton variant="rounded" width={'10%'} height={300} />
            <Skeleton variant="rounded" width={'10%'} height={300} />
            <Skeleton variant="rounded" width={'10%'} height={300} />
            <Skeleton variant="rounded" width={'10%'} height={300} />
            <Skeleton variant="rounded" width={'15%'} height={300} />
          </Container>
        </Container>
      ) : (
        <DataGrid
          autoHeight
          disableColumnFilter
          rows={rows}
          columns={columns}
          rowSelectionModel={visibleSelections}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 },
            },
          }}
          getRowHeight={() => 'auto'} 
          sx={{
            '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': { 
              py: '16px', 
              display: 'flex', 
              alignItems: 'center' 
            },
            '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': { 
              py: '16px', 
              display: 'flex', 
              alignItems: 'center' 
            },
            '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': { 
              py: '22px', 
              display: 'flex', 
              alignItems: 'center' 
            },
          }}          
          // checkboxSelection
          pageSizeOptions={[10, 20, 50, 100]}
          disableColumnMenu={true}
          disableSelectionOnClick={true}
          // onRowSelectionModelChange={handleSelectionChange}
          isRowSelectable={(params) =>
            params.row.latitude !== 0 && params.row.longitude !== 0
          }
          localeText={{
            noRowsLabel: (
              <NoResultsComponent message={"No se encontrarón lotes"} />
            ),
          }}
        />
      )}
    </Container>
  );
};

const NoResultsComponent = ({ message }) => (
  <Container sx={styles.noDataContainer}>
    <SearchOffIcon sx={styles.noDataIcon} />
    <Typography sx={styles.noDataTitle}>{message}</Typography>
  </Container>
);

export default AssignedLotsTable;
